import XMarkIcon from '@heroicons/react/20/solid/XMarkIcon.js';
import React from 'react';

import { tv } from '../../tv';
import { IconButton } from '../icon-button';
import { Text } from '../text';
import { STATUS_ICONS } from './status-icons';

/**
 * A message on its own tinted surface, inline with the content it is about:
 * a sync that needs re-authorising above the integration's settings, a note
 * that a questionnaire is read-only while under review.
 *
 * INLINE only. A message that spans the whole page belongs to a page banner,
 * which is a different component with different rules (full width, page
 * altitude, usually dismissible); stretching a Callout across the page is
 * how the two blur into one. The reasoning, and why this is not called
 * "Alert", is in docs/specs/frontend/design-system/callout.md.
 *
 * The status drives everything: tint, icon and announcement travel together,
 * so a call site says what the message MEANS and can never pair a success
 * tint with a warning icon. The icon is not decoration a caller can swap:
 * WCAG 1.4.1 names colour-only status a failure, so the glyph is the
 * non-colour indicator and the status owns it. It carries aria-hidden
 * because the text already says what it marks.
 */

/* Each status resolves to the semantic trio (bg step 3, border step 7, text
 * step 11), so a palette change moves every callout at once. Neutral is the
 * exception by design: it is not a fifth hue but the absence of one, a
 * recessed note using the surface tokens, which is why it also has no icon
 * (there is no status to mark, and a glyph would promote an aside into an
 * announcement). */
const calloutStyles = tv({
  slots: {
    /* items-start keeps the icon on the first line of a message that wraps;
     * a centred icon beside three lines of text points at none of them.
     * text-sm on the root sets the step once, so the title and the
     * description cannot drift apart in size. */
    root: 'ck:flex ck:items-start ck:gap-2 ck:rounded-3 ck:border ck:p-3 ck:text-sm',
    /* The 20px glyph exactly matches the sm line height, so the icon and the
     * first line of text share a centre with no optical nudging. */
    icon: 'ck:size-5 ck:shrink-0',
    /* min-w-0 lets long unbroken content (a URL, an identifier) wrap instead
     * of pushing the callout wider than the region that holds it. */
    body: 'ck:flex ck:min-w-0 ck:flex-1 ck:flex-col ck:gap-1',
    /* -my-1 lets a control overhang the padding so its label aligns with the first line. */
    trailing: 'ck:-my-1 ck:flex ck:shrink-0 ck:items-center ck:gap-1',
  },
  variants: {
    status: {
      info: { root: 'ck:border-info-border ck:bg-info-bg ck:text-info-text' },
      success: { root: 'ck:border-success-border ck:bg-success-bg ck:text-success-text' },
      warning: { root: 'ck:border-warning-border ck:bg-warning-bg ck:text-warning-text' },
      error: { root: 'ck:border-error-border ck:bg-error-bg ck:text-error-text' },
      neutral: { root: 'ck:border-border-subtle ck:surface-recessed ck:text-text-muted' },
    },
  },
});

interface CalloutBaseProps {
  /**
   * What the message means. Drives the tint, the icon and how assistive
   * technology announces it; `neutral` is an untinted aside with no icon.
   */
  status: 'info' | 'success' | 'warning' | 'error' | 'neutral';
  /** One bolder line above the description, for a message that needs a name. */
  title?: string;
  /** The message itself. */
  children?: React.ReactNode;
  /** One way out of the situation the message describes: retry, re-authorize, view. */
  action?: React.ReactNode;
  id?: string;
}

/* A dismissal always arrives with its name, the way Tag and Status pair the
 * remove handler with `removeLabel`. */
type CalloutDismissProps =
  | { onDismiss: () => void; dismissLabel: string }
  | { onDismiss?: never; dismissLabel?: never };

export type CalloutProps = CalloutBaseProps & CalloutDismissProps;

export const Callout = React.forwardRef<HTMLDivElement, CalloutProps>(function Callout(
  { status, title, children, action, onDismiss, dismissLabel, id },
  ref,
) {
  const { root, icon, body, trailing } = calloutStyles({ status });
  const StatusIcon = status === 'neutral' ? null : STATUS_ICONS[status];
  return (
    <div
      ref={ref}
      id={id}
      className={root()}
      /* warning and error interrupt (role=alert announces immediately);
       * info, success and neutral wait their turn (role=status announces
       * politely). The split is by urgency, not by hue: a warning the user
       * should act on now is the boundary. */
      role={status === 'warning' || status === 'error' ? 'alert' : 'status'}
      data-component="callout"
    >
      {StatusIcon ? <StatusIcon className={icon()} aria-hidden /> : null}
      <div className={body()}>
        {title ? (
          <Text size="inherit" weight="medium" color="inherit">
            {title}
          </Text>
        ) : null}
        {children}
      </div>
      {action || onDismiss ? (
        <div className={trailing()}>
          {action}
          {onDismiss ? (
            <IconButton
              icon={XMarkIcon}
              label={dismissLabel}
              variant="ghost"
              size="sm"
              tooltip={false}
              onPress={onDismiss}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
});
